/**
 * Admin Review Appeal Dashboard
 * Allows admins to review, manage, and resolve appeals
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import {
  Gavel,
  Schedule,
  CheckCircle,
  Cancel,
  ExpandMore,
  AttachFile,
  Message,
  Info,
  Warning,
  Person,
  Email,
  Star
} from '@mui/icons-material';
import DialogCloseButton from '../common/DialogCloseButton';
import reviewAppealService from '../../services/reviewAppealService';
import { formatDate, formatRelativeTime } from '../../utils/helpers';

const ReviewAppealDashboard = () => {
  const [appeals, setAppeals] = useState([]);
  const [filteredAppeals, setFilteredAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState('pending');
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(false);
  const [noteDialog, setNoteDialog] = useState(false);
  const [internalNote, setInternalNote] = useState('');
  const [resolutionData, setResolutionData] = useState({
    resolutionType: '',
    resolutionNotes: '',
    reviewRemoved: false,
    reviewEdited: false,
    ratingAdjusted: false,
    newRating: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadAppeals();
    loadStats();
  }, []);

  useEffect(() => {
    filterAppeals();
  }, [tabValue, appeals]);

  const loadAppeals = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reviewAppealService.getAllAppeals({ status: tabValue === 'all' ? undefined : tabValue });
      
      if (result.success) {
        setAppeals(result.data || []);
      }
    } catch (error) {
      console.error('Error loading appeals:', error);
      setError(error.message || 'Failed to load appeals');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await reviewAppealService.getAppealStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const filterAppeals = () => {
    if (tabValue === 'all') {
      setFilteredAppeals(appeals);
    } else {
      setFilteredAppeals(appeals.filter(a => a.status === tabValue));
    }
  };

  const handleViewDetails = async (appeal) => {
    try {
      const result = await reviewAppealService.getAppeal(appeal.id);
      if (result.success) {
        setSelectedAppeal(result.data);
        setDetailsDialog(true);
      }
    } catch (error) {
      console.error('Error loading appeal details:', error);
      alert(error.message || 'Failed to load appeal details');
    }
  };

  const handleAddInternalNote = async () => {
    if (!internalNote.trim()) return;

    try {
      setSubmitting(true);
      await reviewAppealService.addInternalNote(selectedAppeal.id, internalNote.trim());
      
      setInternalNote('');
      setNoteDialog(false);
      
      // Refresh appeal details
      await handleViewDetails({ id: selectedAppeal.id });
    } catch (error) {
      console.error('Error adding note:', error);
      alert(error.message || 'Failed to add internal note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolveAppeal = async () => {
    if (!resolutionData.resolutionType) {
      alert('Please select a resolution type');
      return;
    }

    if (!resolutionData.resolutionNotes.trim() || resolutionData.resolutionNotes.trim().length < 20) {
      alert('Please provide detailed resolution notes (at least 20 characters)');
      return;
    }

    try {
      setSubmitting(true);
      await reviewAppealService.resolveAppeal(selectedAppeal.id, {
        resolutionType: resolutionData.resolutionType,
        resolutionNotes: resolutionData.resolutionNotes.trim(),
        reviewRemoved: resolutionData.reviewRemoved,
        reviewEdited: resolutionData.reviewEdited,
        ratingAdjusted: resolutionData.ratingAdjusted,
        newRating: resolutionData.newRating ? parseInt(resolutionData.newRating) : null
      });
      
      // Reset form
      setResolutionData({
        resolutionType: '',
        resolutionNotes: '',
        reviewRemoved: false,
        reviewEdited: false,
        ratingAdjusted: false,
        newRating: null
      });
      setResolveDialog(false);
      setDetailsDialog(false);
      
      // Refresh
      await loadAppeals();
      await loadStats();
    } catch (error) {
      console.error('Error resolving appeal:', error);
      alert(error.message || 'Failed to resolve appeal');
    } finally {
      setSubmitting(false);
    }
  };

  const resolutionTypes = [
    { 
      value: 'appeal_upheld_review_removed', 
      label: 'Appeal Upheld - Review Removed',
      description: 'The appeal is valid. Remove the review entirely from public view.'
    },
    { 
      value: 'appeal_upheld_review_edited', 
      label: 'Appeal Upheld - Review Edited',
      description: 'The appeal is valid. Edit the review to remove false/inappropriate content.'
    },
    { 
      value: 'appeal_upheld_rating_adjusted', 
      label: 'Appeal Upheld - Rating Adjusted',
      description: 'The appeal is valid. Adjust the rating to be more fair.'
    },
    { 
      value: 'appeal_denied', 
      label: 'Appeal Denied',
      description: 'The appeal is not valid. The review will remain as is.'
    },
    { 
      value: 'appeal_partially_upheld', 
      label: 'Appeal Partially Upheld',
      description: 'Some concerns are valid. Make limited changes to the review.'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'under_review': return 'info';
      case 'resolved': return 'success';
      case 'withdrawn': return 'default';
      default: return 'default';
    }
  };

  const isUrgent = (appeal) => {
    if (appeal.status !== 'pending' || !appeal.sla_deadline) return false;
    const deadline = new Date(appeal.sla_deadline);
    const now = new Date();
    const hoursRemaining = (deadline - now) / (1000 * 60 * 60);
    return hoursRemaining < 24;
  };

  if (loading && !appeals.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading appeals...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Statistics */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.total_appeals || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Appeals
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {stats.pending || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {stats.upheld_removed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upheld & Removed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.avg_response_time_hours ? `${parseFloat(stats.avg_response_time_hours).toFixed(1)}h` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Response Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => {
          setTabValue(newValue);
          loadAppeals();
        }}>
          <Tab label={`Pending (${stats?.pending || 0})`} value="pending" />
          <Tab label={`Under Review (${stats?.under_review || 0})`} value="under_review" />
          <Tab label={`Resolved (${stats?.resolved || 0})`} value="resolved" />
          <Tab label="All" value="all" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
        </Box>
      ) : filteredAppeals.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Gavel sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No {tabValue !== 'all' ? tabValue : ''} appeals
          </Typography>
        </Box>
      ) : (
        filteredAppeals.map((appeal) => (
          <Card 
            key={appeal.id} 
            variant="outlined" 
            sx={{ 
              mb: 2,
              borderLeft: isUrgent(appeal) ? '4px solid' : 'none',
              borderLeftColor: 'error.main'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {isUrgent(appeal) && (
                      <Chip
                        label="URGENT"
                        color="error"
                        size="small"
                        icon={<Warning />}
                      />
                    )}
                    <Chip
                      label={appeal.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(appeal.status)}
                      size="small"
                    />
                    <Chip
                      label={appeal.appeal_reason.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {appeal.appellant_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {appeal.appellant_email}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Filed {formatRelativeTime(appeal.created_at)}
                    {appeal.sla_deadline && appeal.status === 'pending' && (
                      <> • Deadline: {formatDate(appeal.sla_deadline)}</>
                    )}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleViewDetails(appeal)}
                >
                  Review Appeal
                </Button>
              </Box>

              <Typography variant="body2" sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mb: 1
              }}>
                {appeal.appeal_description}
              </Typography>

              {appeal.supporting_evidence && appeal.supporting_evidence.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {appeal.supporting_evidence.slice(0, 3).map((ev, idx) => (
                    <Chip 
                      key={idx}
                      label={ev.type}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {appeal.supporting_evidence.length > 3 && (
                    <Chip 
                      label={`+${appeal.supporting_evidence.length - 3} more`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Appeal Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => !submitting && setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => !submitting && setDetailsDialog(false)} />
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Appeal Review</Typography>
            <Chip
              label={selectedAppeal?.status.replace('_', ' ').toUpperCase()}
              color={getStatusColor(selectedAppeal?.status)}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAppeal && (
            <Box>
              {/* Appellant Info */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Appellant Information
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedAppeal.appellant_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedAppeal.appellant_email}
                </Typography>
                <Typography variant="body2">
                  <strong>Filed:</strong> {formatDate(selectedAppeal.created_at)}
                </Typography>
                {selectedAppeal.sla_deadline && (
                  <Typography variant="body2">
                    <strong>SLA Deadline:</strong> {formatDate(selectedAppeal.sla_deadline)}
                  </Typography>
                )}
              </Paper>

              {/* Appeal Reason */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Appeal Reason
                </Typography>
                <Chip label={selectedAppeal.appeal_reason.replace('_', ' ')} />
              </Box>

              {/* Appeal Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Detailed Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedAppeal.appeal_description}
                </Typography>
              </Box>

              {/* Evidence */}
              {selectedAppeal.evidence && selectedAppeal.evidence.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>
                      <AttachFile sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Evidence ({selectedAppeal.evidence.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {selectedAppeal.evidence.map((ev, idx) => (
                        <React.Fragment key={ev.id}>
                          <ListItem>
                            <ListItemText
                              primary={<Chip label={ev.evidence_type} size="small" sx={{ mr: 1 }} />}
                              secondary={
                                <>
                                  <Typography variant="body2">{ev.description}</Typography>
                                  {ev.file_url && (
                                    <Button size="small" href={ev.file_url} target="_blank" rel="noopener">
                                      View File
                                    </Button>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                          {idx < selectedAppeal.evidence.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Messages */}
              {selectedAppeal.messages && selectedAppeal.messages.length > 0 && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>
                      <Message sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Messages ({selectedAppeal.messages.filter(m => !m.is_internal).length})
                      {selectedAppeal.messages.filter(m => m.is_internal).length > 0 && (
                        <> | Internal Notes ({selectedAppeal.messages.filter(m => m.is_internal).length})</>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {selectedAppeal.messages.map((msg, idx) => (
                        <React.Fragment key={msg.id}>
                          <ListItem
                            sx={{
                              bgcolor: msg.is_internal ? 'warning.lighter' : 'transparent',
                              borderRadius: 1,
                              mb: 1
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {msg.sender_name || 'System'}
                                  </Typography>
                                  {msg.is_internal && (
                                    <Chip label="Internal Note" size="small" color="warning" />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {msg.message}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {formatRelativeTime(msg.created_at)}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDetailsDialog(false)}>
            Close
          </Button>
          {selectedAppeal?.status === 'pending' && (
            <>
              <Button
                onClick={() => setNoteDialog(true)}
                variant="outlined"
              >
                Add Internal Note
              </Button>
              <Button
                onClick={() => setResolveDialog(true)}
                variant="contained"
                color="primary"
              >
                Resolve Appeal
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Internal Note Dialog */}
      <Dialog open={noteDialog} onClose={() => !submitting && setNoteDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => !submitting && setNoteDialog(false)} />
        <DialogTitle>Add Internal Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Internal notes are only visible to administrators.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Add your notes here..."
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddInternalNote}
            disabled={submitting || !internalNote.trim()}
            variant="contained"
          >
            {submitting ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Appeal Dialog */}
      <Dialog open={resolveDialog} onClose={() => !submitting && setResolveDialog(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => !submitting && setResolveDialog(false)} />
        <DialogTitle>Resolve Appeal</DialogTitle>
        <DialogContent dividers>
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2 }}>
              Resolution Type *
            </FormLabel>
            <RadioGroup
              value={resolutionData.resolutionType}
              onChange={(e) => setResolutionData({ ...resolutionData, resolutionType: e.target.value })}
            >
              {resolutionTypes.map((type) => (
                <Box
                  key={type.value}
                  sx={{
                    border: '1px solid',
                    borderColor: resolutionData.resolutionType === type.value ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: resolutionData.resolutionType === type.value ? 'primary.lighter' : 'transparent'
                  }}
                >
                  <FormControlLabel
                    value={type.value}
                    control={<Radio />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {type.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, p: 2, width: '100%' }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Resolution Notes *"
            placeholder="Provide detailed notes explaining your decision. This will be visible to the appellant."
            value={resolutionData.resolutionNotes}
            onChange={(e) => setResolutionData({ ...resolutionData, resolutionNotes: e.target.value })}
            disabled={submitting}
            helperText={`${resolutionData.resolutionNotes.length}/1000 characters (minimum 20)`}
            inputProps={{ maxLength: 1000 }}
            sx={{ mb: 3 }}
          />

          {resolutionData.resolutionType && resolutionData.resolutionType.includes('upheld') && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Since you're upholding the appeal, the review will be marked accordingly and actions taken as specified.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResolveDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveAppeal}
            disabled={submitting || !resolutionData.resolutionType || resolutionData.resolutionNotes.trim().length < 20}
            variant="contained"
            color="primary"
          >
            {submitting ? 'Resolving...' : 'Resolve Appeal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewAppealDashboard;

